import { LucideIcon } from 'lucide-react';
import { DashboardTravelRequest } from '@/lib/models/dashboard';
import { UserRole } from '@/lib/auth/models/auth.model';
import type { StatusFilterTone } from '@/components/ui/status-filter';
import type {
  DashboardItineraryPhase,
  DashboardItineraryTab,
  DashboardTravelRequestView,
} from '@/integrations/supabase/rpc-types';

/** Righe per pagina: vicino ai 10 delle altre tabelle, divisibile per 2, 3 e 4 (colonne della griglia). */
export const DASHBOARD_PAGE_SIZE = 12;

// ─────────────────────────────────────────────
// Shared i18n bag for empty/error states
// ─────────────────────────────────────────────
export interface TabI18n {
  /** empty title (no designer filter active) */
  emptyTitleKey: string;
  /** empty title when designer filter is active */
  emptyTitleDesignerKey?: string;
  /** empty description (no designer filter active) */
  emptyDescKey?: string;
  /** empty description when designer filter is active */
  emptyDescDesignerKey?: string;
  /** error loading */
  errorKey: string;
}

// ─────────────────────────────────────────────
// Itinerary tab config (pure data – no state setters)
// ─────────────────────────────────────────────
export interface ItineraryTabConfig {
  value: DashboardItineraryTab;
  labelKey: string;
  icon: LucideIcon;
  /** roles that can see this tab */
  visibleTo: string[];
  /** filtri consulente e cliente (consulente solo per admin e agency) */
  showDesignerFilter: boolean;
  /**
   * Pillole di stato della tab, la prima e' quella di partenza. Servono alle tab che
   * tengono insieme stati con lavori diversi: bozze e archiviati, proposte inviate e
   * rimandate indietro, viaggi da preparare e viaggi in corso.
   */
  phases?: ItineraryPhaseOption[];
  i18n: TabI18n;
}

/** 'all' = nessun filtro sul sotto-stato. */
export type ItineraryPhaseFilter = 'all' | DashboardItineraryPhase;

export interface ItineraryPhaseOption {
  value: ItineraryPhaseFilter;
  labelKey: string;
  tone: StatusFilterTone;
  /** Titolo della lista vuota con questa pillola attiva; assente = quello della tab. */
  emptyTitleKey?: string;
}

// ─────────────────────────────────────────────
// Props for DashboardItinerariesTab
// La tab chiede da sola la propria pagina; filtri e pagina corrente vivono dentro.
// ─────────────────────────────────────────────
export interface ItineraryTabProps {
  config: ItineraryTabConfig;
  userRole: string | null;
  /** Totale della tab senza filtri, lo stesso del badge */
  tabTotal: number;
  /** Cambia quando la pagina chiede di ricaricare tutto */
  refreshKey: number;
  onRefresh: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => Promise<any>;
  onDuplicate: (id: string) => Promise<any>;
  onViewTravelRequest: (id: string) => void;
  onUpdateStatus: (id: string, status: any) => void;
}

// ─────────────────────────────────────────────
// Props for DashboardRequestsTab
// La tab chiede da sola la propria pagina; ordinamento, filtri e pagina vivono dentro.
// ─────────────────────────────────────────────
export interface RequestsTabI18n {
  /** titolo dell'empty state */
  emptyTitleKey: string;
  /** descrizione dell'empty state */
  emptyDescKey?: string;
}

export interface RequestsTabProps {
  view: DashboardTravelRequestView;
  userRole: UserRole | null;
  /** Cambia quando la pagina chiede di ricaricare tutto */
  refreshKey: number;
  onRefresh: () => void;
  onViewRequest: (request: DashboardTravelRequest) => void;
  onCreateItinerary: (request: DashboardTravelRequest) => void;
  i18n: RequestsTabI18n;
}
